/**
 * System Downtime Management Component
 * For IT/Admin to view and resolve employee system downtime reports
 * 
 * PURPOSE: Manage short-term work blocking status reports
 * DISTINCT FROM: IT Support tickets (formal issue tracking)
 * V1 REQUIREMENT: All timestamps displayed in America/New_York (ET)
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  AlertTriangle, CheckCircle, Clock, Loader2, Server, Monitor,
  Cloud, HardDrive, WifiOff, User, Flag, MessageSquare, RefreshCw, ZapOff, Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { formatDateTimeShortET } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL || '';

const REASON_ICONS = {
  system_outage: Server, network_vpn: WifiOff, external_dependency: Cloud,
  software_crash: Monitor, hardware_issue: HardDrive, other: AlertTriangle
};

const formatDuration = (minutes) => {
  if (!minutes && minutes !== 0) return '-';
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
};

// V1 REQUIREMENT: Use ET timezone for all timestamps
const formatDateTime = (isoString) => isoString ? formatDateTimeShortET(isoString) : '-';

const SystemDowntimeManagement = ({ currentUser }) => {
  const [activeReports, setActiveReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolving, setResolving] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [showResolveModal, setShowResolveModal] = useState(false);

  const canManageDowntime = ['admin', 'it', 'it_support', 'it_team'].includes(currentUser?.role);

  const fetchActiveReports = useCallback(async () => {
    if (!canManageDowntime) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/it-downtime/active-issues`);
      setActiveReports(response.data.active_issues || []);
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Failed to load active downtime reports');
    } finally {
      setLoading(false);
    }
  }, [canManageDowntime]);

  useEffect(() => {
    fetchActiveReports();
    const interval = setInterval(fetchActiveReports, 30000);
    return () => clearInterval(interval);
  }, [fetchActiveReports]);

  const handleResolveDowntime = async () => {
    if (!selectedReport) return;
    try {
      setResolving(true);
      const response = await axios.post(`${API}/api/it-downtime/resolve`, {
        downtime_id: selectedReport.id,
        resolution_notes: resolutionNotes.trim() || null
      });
      toast.success(response.data.message, {
        description: response.data.flagged_long_duration ? '⚠️ Flagged for review (>4 hours)' : 'Session tracking resumed'
      });
      setShowResolveModal(false);
      setSelectedReport(null);
      setResolutionNotes('');
      fetchActiveReports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resolve downtime');
    } finally {
      setResolving(false);
    }
  };

  if (!canManageDowntime) return null;

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <ZapOff className="w-5 h-5 text-amber-600" />
              System Downtime Reports
            </CardTitle>
            <CardDescription>Employees blocked from working due to technical issues</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchActiveReports} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && activeReports.length === 0 ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : activeReports.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Zap className="w-12 h-12 mx-auto mb-2 text-green-500" />
            <p className="font-medium">No Active Downtime</p>
            <p className="text-sm">All employees are working normally</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Reported</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeReports.map((report) => {
                const ReasonIcon = REASON_ICONS[report.reason_id] || AlertTriangle;
                const isLong = report.elapsed_minutes > 240;
                return (
                  <TableRow key={report.id} className={isLong ? 'bg-red-50' : ''}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="font-medium">{report.user_name}</p>
                          <p className="text-xs text-gray-500">{report.started_by_role}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <ReasonIcon className="w-4 h-4 text-amber-600" />
                        <span>{report.reason_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">{report.description || '-'}</TableCell>
                    <TableCell className="text-sm">{formatDateTime(report.start_time)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-gray-400" />
                        <span className={isLong ? 'text-red-600 font-medium' : ''}>{formatDuration(report.elapsed_minutes)}</span>
                        {isLong && <Flag className="w-3 h-3 text-red-500 ml-1" />}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant={isLong ? 'destructive' : 'default'} onClick={() => { setSelectedReport(report); setResolutionNotes(''); setShowResolveModal(true); }}>
                        <CheckCircle className="w-4 h-4 mr-1" />Resolve
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {activeReports.length > 0 && (
          <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
            <span>{activeReports.length} employee{activeReports.length !== 1 ? 's' : ''} blocked</span>
            <span>{activeReports.filter(r => r.elapsed_minutes > 240).length} flagged for review</span>
          </div>
        )}
      </CardContent>

      <Dialog open={showResolveModal} onOpenChange={setShowResolveModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Resolve System Downtime
            </DialogTitle>
            <DialogDescription>Mark this downtime as resolved. Session tracking will resume.</DialogDescription>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-4 py-2">
              <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                <div className="flex justify-between"><span className="text-sm text-gray-600">Employee</span><span className="font-medium">{selectedReport.user_name}</span></div>
                <div className="flex justify-between"><span className="text-sm text-gray-600">Reason</span><span className="font-medium">{selectedReport.reason_name}</span></div>
                <div className="flex justify-between"><span className="text-sm text-gray-600">Duration</span><span className={`font-medium ${selectedReport.elapsed_minutes > 240 ? 'text-red-600' : ''}`}>{formatDuration(selectedReport.elapsed_minutes)}</span></div>
                {selectedReport.description && <div><span className="text-sm text-gray-600">Description</span><p className="text-sm mt-1">{selectedReport.description}</p></div>}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 flex items-center gap-1"><MessageSquare className="w-4 h-4" />Resolution Notes (optional)</label>
                <Textarea placeholder="What was the issue and how was it resolved?" value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)} className="mt-1.5 min-h-[80px]" maxLength={500} />
                <p className="text-xs text-gray-500 mt-1 text-right">{resolutionNotes.length}/500</p>
              </div>
              {selectedReport.elapsed_minutes > 240 && (
                <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-md">
                  <Flag className="w-4 h-4 text-red-500 mt-0.5" />
                  <div><p className="text-sm font-medium text-red-700">Flagged for Review</p><p className="text-xs text-red-600">Duration exceeded 4 hours</p></div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowResolveModal(false); setSelectedReport(null); setResolutionNotes(''); }}>Cancel</Button>
            <Button onClick={handleResolveDowntime} disabled={resolving} className="bg-green-600 hover:bg-green-700">
              {resolving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <CheckCircle className="w-4 h-4 mr-1" />Resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SystemDowntimeManagement;
